import { useAuthenticator, withAuthenticator } from '@aws-amplify/ui-react';
import { Auth, I18n } from 'aws-amplify';
import { useEffect } from 'react';
import { BrowserRouter } from 'react-router-dom';
import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import { Toaster } from 'react-hot-toast';
import {
  Chart as ChartJS,
  ArcElement, Tooltip,
  Legend,
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  BarElement,
  BarController,
} from 'chart.js';
import PageWrapper from './components/layout/PageWrapper/PageWrapper';
import { LoginComponents, formFields } from './components/common/amplifyLogin/AmplifyLogin';
import Main from './components/layout/Main/Main';
import awsconfig from './aws-exports';
import SideMenu from './components/layout/SideMenu/SideMenu';
import Header from './components/layout/Header/Header';
import ScrollRestoration from './components/common/ScrollRestoration/ScrollRestoration';
import {
  DataAPIService,
  ProfileAPIService,
  VideoAPIService,
  FotaAPIService,
  NotificationAPIService,
  GeofenceAPIService,
  ReportAPIService,
} from './API/services/AxiosAPIService';
import { removeDeprecatedStorageData } from './utils/storageUtils';
import { recordLogin } from './API/hooks/users/useRecordLogin';
import useCrossTabAuthSync from './utils/hooks/useCrossTabAuthSync';

const queryClient = new QueryClient();
// Auth.configure({ ...awsconfig, storage: sessionStorage }); //using sessionStorage for saving of Cognito data
Auth.configure(awsconfig);
console.info('APP VERSION', process.env.REACT_APP_VERSION);

// can't find a way to validate input fields on a client
// but we can modify an error message to at least make it more human readable

I18n.setLanguage('en');
I18n.putVocabulariesForLanguage('en', {
  'Sign in': 'Log in',
  'Signing in': 'Logging...',
  'Incorrect username or password.': 'Incorrect email or password.',
  'Username/client id combination not found.': 'Email not found.',
  '1 validation error detected: Value at \'password\' failed to satisfy constraint: Member must satisfy regular expression pattern: ^[\\S]+.*[\\S]+$': 'Password is too weak',
  'User password cannot be reset in the current state.': 'Password reset is not available for this account. Please contact your administrator.',
});

ChartJS.register(
  ArcElement,
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  BarElement,
  BarController,
  Tooltip,
  Legend,
);

function App(): JSX.Element {
  const { user, signOut } = useAuthenticator((context) => [context.user, context.signOut]);
  useCrossTabAuthSync(signOut);
  const token = user.getSignInUserSession()
    ?.getIdToken()
    ?.getJwtToken() || '';

  DataAPIService.setAuthToken(token);
  ProfileAPIService.setAuthToken(token);
  VideoAPIService.setAuthToken(token);
  FotaAPIService.setAuthToken(token);
  NotificationAPIService.setAuthToken(token);
  GeofenceAPIService.setAuthToken(token);
  ReportAPIService.setAuthToken(token);

  removeDeprecatedStorageData();

  useEffect(() => {
    if (token) {
      recordLogin();
    }
  }, [token]);

  return (
    <QueryClientProvider client={queryClient}>
      <PageWrapper>
        <BrowserRouter>
          <ScrollRestoration />
          <SideMenu />
          <Header />
          <Main />
        </BrowserRouter>
      </PageWrapper>
      <Toaster
        position="bottom-right"
      />
    </QueryClientProvider>
  );
}

export default withAuthenticator(App, {
  hideSignUp: true,
  components: LoginComponents,
  variation: 'default',
  loginMechanisms: ['email'],
  formFields,
});
